import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Lock, UserCircle, Hash, Mail, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const isNativeApp = !!window.Capacitor?.isNativePlatform?.();

const NightSkyBg = () => (
  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #0d1b2a 40%, #1b2838 100%)' }}>
    {[...Array(30)].map((_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: `${i % 5 === 0 ? 2 : 1}px`, height: `${i % 5 === 0 ? 2 : 1}px`,
        left: `${(i * 23 + 7) % 96}%`, top: `${(i * 17 + 3) % 94}%`,
        opacity: 0.2 + (i % 4) * 0.15,
        background: i % 5 === 0 ? 'rgba(212,175,55,0.9)' : 'white',
        animation: `regTwinkle ${2 + (i % 3) * 1.5}s ease-in-out infinite ${i * 0.3}s`
      }} />
    ))}
    <style>{`@keyframes regTwinkle { 0%, 100% { opacity: 0.1; } 50% { opacity: 0.8; } }`}</style>
  </div>
);

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    email: '',
    inviteCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(formData.username, formData.password, formData.displayName, formData.inviteCode, formData.email);
    setLoading(false);

    if (result.success) {
      if (result.isFirstUser) {
        toast.success(`Welcome Admin! You're the first user of AurumChat.`);
      } else {
        toast.success(`Welcome ${result.user.display_name}!`);
      }
      navigate('/chats');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col relative ${isNativeApp ? '' : 'bg-background-DEFAULT'}`} data-testid="register-page">
      {isNativeApp && <NightSkyBg />}
      <div className="relative z-10 p-6">
        <button data-testid="back-btn" onClick={() => navigate('/')} className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${isNativeApp ? 'hover:bg-white/10' : 'hover:bg-secondary-DEFAULT'}`}>
          <ArrowLeft size={20} className={isNativeApp ? 'text-white' : 'text-text-primary'} />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className={`rounded-3xl p-8 ${isNativeApp ? 'bg-white/10 backdrop-blur-lg border border-white/10' : 'bg-white shadow-card border border-border-DEFAULT'}`}>
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-gold mx-auto mb-4 ${isNativeApp ? 'bg-[#D4AF37]/20' : 'gold-gradient'}`}>
                <UserCircle size={32} className={isNativeApp ? 'text-[#D4AF37]' : 'text-white'} />
              </div>
              <h2 className={`font-heading text-3xl mb-2 ${isNativeApp ? 'text-white' : 'text-text-primary'}`}>Create Account</h2>
              <p className={`text-sm ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`}>Join the secure conversation</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>
                  Invite Code <span className="text-xs text-gray-500">(Optional for first user)</span>
                </label>
                <div className="relative">
                  <Hash size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="invite-code-input" type="text" value={formData.inviteCode} onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all font-mono tracking-wider ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="XXXXXXXX" maxLength={8} />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Display Name</label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="display-name-input" type="text" value={formData.displayName} onChange={(e) => setFormData({ ...formData, displayName: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Your name" required />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Username</label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="username-input" type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Choose a unique username" required />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Email <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-4 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="your@email.com" required />
                </div>
                <p className="text-xs text-amber-500 mt-1">Used for password recovery</p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="password-input" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-12 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="At least 6 characters" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isNativeApp ? 'text-gray-300' : 'text-text-primary'}`}>Confirm Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="confirm-password-input" type={showPassword ? "text" : "password"} value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className={`w-full h-12 border-none rounded-xl pl-12 pr-12 placeholder:text-gray-500 focus:ring-1 focus:ring-[#D4AF37] transition-all ${isNativeApp ? 'bg-white/10 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Re-enter password" required />
                </div>
              </div>

              <button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full h-12 px-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium shadow-gold hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-5 text-center">
              <p className={`text-sm ${isNativeApp ? 'text-gray-400' : 'text-text-secondary'}`}>
                Already have an account?{' '}
                <button onClick={() => navigate('/login')} className="text-[#D4AF37] font-medium hover:underline">Sign in</button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;